import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form, OverlayTrigger, Spinner, Table, Tooltip } from "react-bootstrap";
import { ApplicationState } from "../../../../../store";
import { useDispatch, useSelector } from "react-redux";
import {
  createDataExtract,
  getAllDistricts,
  getDataPeriodsByDistrictId
} from "../../../../../store/superuser/actions";
import {
  SuperuserState,
  Data_Extract_Request,
  Data_Fetch_Request,
  DataPeriodDetailsByDistrict,
} from "../../../../../store/superuser/types";
import {
  FileTypeOptions, TimePeriod, TimePeriodOptions,
} from "../../../../../store/groups/types";
import Select from "react-select";
import { ReactSelectOption } from "../../../../../store/onboarding/types";
import { toastr } from "react-redux-toastr";
import DatePicker from "react-datepicker";
import { dateToFormatString, parseDateOnlyLocal } from "../../../../utils/DateTimeUtils";
import LoadingIndicator from "../../../../common/onboarding/LoadingIndicator";
import moment from "moment";
import { UserAssignmentRoleDisplayedNames } from "../../../../../store/auth/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faQuestionCircle } from "@fortawesome/free-solid-svg-icons";

// State for fetching data periods
interface DataFetchFormState {
  district_ids: number[];
  start_date: string;
  end_date: string;
}

// State for data extraction
interface DataExtractFormState {
  data_period_ids: number[];
  file_type: string;
  receiver_email: string;
  extract_year: number;
  year: string,
  selected_start_date: string,
  selected_end_date: string,
  selected_start_month: string,
  selected_end_month: string,
}

// Function to calculate auto date range based on current date
const getAutoDateRange = () => {
  const today = new Date();
  const currentMonth = today.getMonth(); // 0-11 (Jan-Dec)
  const currentYear = today.getFullYear();
  
  let startDate: Date;
  let endDate: Date;
  
  if (currentMonth >= 0 && currentMonth <= 4) {
    // January (0) to May (4): Start = Jan 1, End = May 31
    startDate = new Date(currentYear, 0, 1); // Jan 1
    endDate = new Date(currentYear, 4, 31); // May 31
  } else if (currentMonth >= 5 && currentMonth <= 6) {
    // June (5) to July (6): Start = June 1, End = current date
    startDate = new Date(currentYear, 5, 1); // June 1
    endDate = today;
  } else {
    // August (7) to December (11): Start = Aug 1, End = Dec 31
    startDate = new Date(currentYear, 7, 1); // Aug 1
    endDate = new Date(currentYear, 11, 31); // Dec 31
  }
  
  return {
    start_date: dateToFormatString(startDate, "YYYY-MM-DD"),
    end_date: dateToFormatString(endDate, "YYYY-MM-DD")
  };
};

const defaultFetchValue: DataFetchFormState = {
  district_ids: [],
  ...getAutoDateRange()
};

const defaultExtractValue: DataExtractFormState = {
  data_period_ids: [],
  file_type: "",
  receiver_email: "",
  extract_year: new Date().getFullYear(),
  year: "",
  selected_start_date: '',
  selected_end_date: '',
  selected_start_month: '',
  selected_end_month: '',
};

// Restrict date pickers' year dropdown to current year + 1 (Dec 31 of next year)
const maxSelectableDate = new Date(new Date().getFullYear(), 11, 31);



const DataExtractForm = () => {
  const {
    districts
  } = useSelector<ApplicationState, SuperuserState>((s) => s.superuser);

  const dispatch = useDispatch();

  // Separate states for fetch and extract functionality
  const [dataFetch, setDataFetch] = useState<DataFetchFormState>(defaultFetchValue);
  const [dataExtract, setDataExtract] = useState<DataExtractFormState>(defaultExtractValue);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dataPeriodDetails, setDataPeriodDetails] = useState<DataPeriodDetailsByDistrict[]>([]);

  useEffect(() => {
    dispatch(getAllDistricts());
  }, []);

  const isDataExtractLoading = useSelector((s: ApplicationState) => s.superuser.isLoading.createDataExtract)

  // Generate year options from last 5 years
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years: ReactSelectOption<any>[] = [];
    for (let year = (currentYear - 5); year <= currentYear; year++) {
      years.push({
        value: year,
        label: year.toString()
      });
    }
    return years;
  }, []);

  let districtsArray = useMemo(() => {
    let districtArr: ReactSelectOption<any>[] = [];
    districtArr = districts.map((district) => {
      return {
        value: district.id,
        label: district.name +' - '+ district.id,
      }
    })
    return districtArr;
  },[districts])



  const handleClose = useCallback(() => {
    setDataExtract(defaultExtractValue);
  }, []);

  const handleStartDate = useCallback((date: Date) => {
    const newStartDate = dateToFormatString(date, "YYYY-MM-DD");
    setDataFetch((prevState) => {
      // If end date is empty, auto-set it to today.
      // If end date already exists but is now before the new start date,
      // clear it so the user must re-pick a valid end date instead of
      // silently carrying forward an invalid range.
      let end_date = prevState.end_date;
      if (!end_date) {
        end_date = dateToFormatString(new Date(), "YYYY-MM-DD");
      } else if (end_date < newStartDate) {
        end_date = '';
      }
      return {
        ...prevState,
        start_date: newStartDate,
        end_date
      };
    });
    // Filters changed - any previously fetched data periods are now stale.
    setDataPeriodDetails([]);
    setDataExtract(defaultExtractValue);
  }, []);

  const handleEndDate = useCallback((date: Date) => {
    setDataFetch((prevState) => ({
      ...prevState,
      end_date: dateToFormatString(date, "YYYY-MM-DD")
    }));
    // Filters changed - any previously fetched data periods are now stale.
    setDataPeriodDetails([]);
    setDataExtract(defaultExtractValue);
  }, []);

  const handleSubmit = useCallback((event: React.FormEvent) => {
    event.preventDefault();
    if (dataFetch.district_ids.length > 0
      && dataExtract.data_period_ids.length > 0
      && dataExtract.file_type
      && dataFetch.start_date
      && dataFetch.end_date
      && dataExtract.receiver_email
      && dataExtract.extract_year
    ) {
      if(dataFetch.start_date > dataFetch.end_date) {
        toastr.error('Alert!!', 'Start date should be less than end date')
        return;
      }
      
      // Prepare the data for API call
      const startDateObj = parseDateOnlyLocal(dataFetch.start_date);
      const endDateObj = parseDateOnlyLocal(dataFetch.end_date);
      const requestData: Data_Extract_Request = {
        data_period_ids: dataExtract.data_period_ids,
        file_type: dataExtract.file_type,
        receiver_email: dataExtract.receiver_email,
        extract_year: dataExtract.extract_year,
        selected_start_date: startDateObj?.getDate().toString() ?? '',
        selected_start_month: ((startDateObj?.getMonth() ?? 0) + 1).toString(),
        selected_start_year: startDateObj?.getFullYear().toString() ?? '',
        selected_end_date: endDateObj?.getDate().toString() ?? '',
        selected_end_month: ((endDateObj?.getMonth() ?? 0) + 1).toString(),
        selected_end_year: endDateObj?.getFullYear().toString() ?? '',
      };
      
      dispatch<any>(createDataExtract(requestData));
    }
  }, [dataFetch.district_ids.length, dataFetch.start_date, dataFetch.end_date, dataExtract, dispatch]);

  const getDataPeriodsByDistrict = useCallback(() => {
    if(dataFetch.district_ids.length > 0 && dataFetch.start_date && dataFetch.end_date) {
      if(dataFetch.start_date > dataFetch.end_date) {
        toastr.error('Alert!!', 'Start date should be less than end date');
        return;
      }
      setIsLoading(true);
      
      // Prepare request data with district_ids array and date range
      const requestData: Data_Fetch_Request = {
        district_ids: dataFetch.district_ids,
        start_date: dataFetch.start_date,
        end_date: dataFetch.end_date
      };
      
      // Make a single API call with all district IDs and date range
      dispatch<any>(getDataPeriodsByDistrictId(requestData))
        .then((dataPeriods: DataPeriodDetailsByDistrict[]) => {
          setIsLoading(false);
          if(dataPeriods.length) {
            // Remove duplicates by id (if any)
            const uniqueDataPeriods = dataPeriods.filter((item, index, self) => 
              index === self.findIndex((t) => t.id === item.id)
            );
            setDataPeriodDetails(uniqueDataPeriods);
            setDataExtract(defaultExtractValue);
          } else {
            setDataPeriodDetails([]);
            toastr.info('Info', 'No data periods found for the selected district(s) and date range');
          }
        }).catch((errorMsg: string) => {
          setIsLoading(false);
          toastr.error('Failed', errorMsg);
        });
    } else {
      setIsLoading(false);
      toastr.error('Alert', 'Please select at least one district and date range to get data periods')
    }
  }, [dataFetch.district_ids, dataFetch.start_date, dataFetch.end_date, dispatch]);

  const toggleDataPeriodSelection = useCallback((dataPeriodId: number) => {
    setDataExtract((prevState) => {
      const isSelected = prevState.data_period_ids.includes(dataPeriodId);
      return {
        ...prevState,
        data_period_ids: isSelected
          ? prevState.data_period_ids.filter(id => id !== dataPeriodId)
          : [...prevState.data_period_ids, dataPeriodId]
      };
    });
  }, []);

  const handleResetDataPeriods = useCallback(() => {
    setDataPeriodDetails([]);
    setDataExtract(defaultExtractValue);
    setDataFetch((prevState) => ({
      ...prevState,
      district_ids: [],
      start_date: '',
      end_date: ''
    }));
  }, []);

  return (
    <div className="d-flex justify-content-center">
      <div className="col-12 onboardingContainer">
        <h2 className="p-2 mb-2 purpleModalHeader rounded">Data Extract</h2>
        <Form id="dataExtractForm" onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <div className="row">
              <div className="col-12">
                <Form.Label className="font-weight-bold" column={false}>
                  Districts
                </Form.Label>
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  value={districtsArray.filter((d) => dataFetch.district_ids.includes(d.value))}
                  menuPlacement="auto"
                  menuPortalTarget={document.body}
                  options={districtsArray}
                  isMulti
                  styles={{
                    menuPortal: (styles) => ({ ...styles, zIndex: 9999 }),
                  }}
                  openMenuOnFocus
                  onChange={(selectedOptions: any) => 
                    setDataFetch((prevState) => ({ 
                      ...prevState, 
                      district_ids: selectedOptions ? selectedOptions.map((opt: any) => opt.value) : []
                    }))}
                />
              </div>
            </div>
            <div className="row mt-4">
              <div className="col-2">
                <Form.Label className="font-weight-bold" column={false}>
                  Start Date
                </Form.Label>
                <DatePicker
                  selected={parseDateOnlyLocal(dataFetch.start_date)}
                  onChange={handleStartDate}
                  selectsStart
                  startDate={parseDateOnlyLocal(dataFetch.start_date)}
                  endDate={parseDateOnlyLocal(dataFetch.end_date)}
                  className="form-control"
                  placeholderText="Select.."
                  dateFormat="MMM d, yyyy"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  popperPlacement="bottom-start"
                  calendarClassName="custom-calendar-wide"
                  popperClassName="datepicker-popper-high-z"
                  maxDate={maxSelectableDate}
                />
              </div>
              <div className="col-2">
                <Form.Label className="font-weight-bold" column={false}>
                  End Date
                </Form.Label>
                <DatePicker
                  selected={parseDateOnlyLocal(dataFetch.end_date)}
                  onChange={handleEndDate}
                  selectsEnd
                  startDate={parseDateOnlyLocal(dataFetch.start_date)}
                  endDate={parseDateOnlyLocal(dataFetch.end_date)}
                  minDate={parseDateOnlyLocal(dataFetch.start_date)}
                  className="form-control"
                  placeholderText="Select.."
                  dateFormat="MMM d, yyyy"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  popperPlacement="bottom-start"
                  calendarClassName="custom-calendar-wide"
                  popperClassName="datepicker-popper-high-z"
                  maxDate={maxSelectableDate}
                />
              </div>
              <div className="col-8 d-flex justify-content-end align-items-end">
                <button
                  type="button"
                  className="whiteBtnSm"
                  onClick={handleResetDataPeriods}
                  disabled={dataFetch.district_ids.length === 0 && !dataFetch.start_date && !dataFetch.end_date}
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="blueBtnSm ml-2"
                  onClick={getDataPeriodsByDistrict}
                  disabled={dataFetch.district_ids.length === 0
                    || !dataFetch.start_date
                    || !dataFetch.end_date
                    || isLoading
                  }
                >
                  Fetch Data Periods
                  {isLoading && <Spinner animation="border" size="sm" />}
                </button>
              </div>
            </div>
          </Form.Group>
          {dataPeriodDetails.length ? 
          <>
            <div className="data-period-details">
              <Form.Group className="mb-3">
                <div className="row">
                  <div className="col-3">
                    <Form.Label className="font-weight-bold" column={false}>
                      File Type
                      <OverlayTrigger
                        trigger={["hover", "focus"]}
                        placement="top"
                        overlay={
                          <Tooltip className="customTooltip" id="tooltip-top">
                            Data will be extracted within the above date range.
                          </Tooltip>
                        }
                      >
                        <span className="ml-1" style={{ cursor: "pointer" }}>
                          <FontAwesomeIcon icon={faQuestionCircle} />
                        </span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Select
                      className="react-select-container"
                      classNamePrefix="react-select"
                      value={dataExtract.file_type ? FileTypeOptions.find((s) => s.value === dataExtract.file_type) : null}
                      menuPlacement="auto"
                      menuPortalTarget={document.body}
                      options={FileTypeOptions}
                      styles={{
                        menuPortal: (styles) => ({ ...styles, zIndex: 9999 }),
                      }}
                      openMenuOnFocus
                      onChange={(selectedOption:any) => 
                        setDataExtract((prevState) => ({ ...prevState, file_type: selectedOption.value}))}
                    />
                  </div>
                  <div className="col-3">
                    <Form.Label className="font-weight-bold" column={false}>
                      Data Extract Year
                      <OverlayTrigger
                        trigger={["hover", "focus"]}
                        placement="top"
                        overlay={
                          <Tooltip className="customTooltip" id="tooltip-top">
                            Please select a year which you want in the file.
                          </Tooltip>
                        }
                      >
                        <span className="ml-1" style={{ cursor: "pointer" }}>
                          <FontAwesomeIcon icon={faQuestionCircle} />
                        </span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Select
                      className="react-select-container"
                      classNamePrefix="react-select"
                      value={yearOptions.find((y) => y.value === dataExtract.extract_year)}
                      menuPlacement="auto"
                      menuPortalTarget={document.body}
                      options={yearOptions}
                      styles={{
                        menuPortal: (styles) => ({ ...styles, zIndex: 9999 }),
                      }}
                      openMenuOnFocus
                      onChange={(selectedOption:any) => 
                        setDataExtract((prevState) => ({ ...prevState, extract_year: selectedOption.value}))}
                    />
                  </div>
                  <div className="col-6">
                    <Form.Label className="font-weight-bold">
                      Data Extract Receiver Email
                    </Form.Label>
                    <Form.Control
                      required
                      type="email"
                      placeholder="Enter email"
                      name="email"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                        setDataExtract((prevState) => ({ ...prevState, receiver_email: e.target.value}))}
                      value={dataExtract.receiver_email}
                    />
                  </div>
                </div>
              </Form.Group>
              <div className="col-12 d-flex justify-content-end mt-4">
                <button
                  className="whiteBtnSm"
                  type="reset"
                  onClick={handleClose}
                >
                  Reset
                </button>
                <button
                  className="blueBtnSm ml-2"
                  type="submit"
                  title={dataExtract.data_period_ids.length === 0 ? "select at least one data period" : ""}
                  disabled={(dataExtract.data_period_ids.length === 0
                    || (dataExtract.file_type === '') 
                    || (dataExtract.receiver_email === '')
                    || !dataExtract.extract_year)
                    || !dataFetch.start_date
                    || !dataFetch.end_date
                    || isDataExtractLoading
                  }
                >
                  Send 
                  {isDataExtractLoading && <Spinner animation="border" size="sm" />}
                </button>
              </div>
              {isLoading ? <div className="px-3 mt-3"><LoadingIndicator type="table" rows={10} columns={7} /></div>
              : dataPeriodDetails.length ? (
                <div className="row px-3 mt-3">
                  <div className="col-12 px-0">
                    <div className="table-responsive data-periods-table-container">
                      <Table className="data-periods-table" bordered>
                        <thead className="purpleTableHeader">
                          <tr>
                            <th>District Name</th>
                            <th>Data Period</th>
                            <th>Time Period</th>
                            <th>Created</th>
                            <th>Owner</th>  
                            <th>User Type</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dataPeriodDetails.map((dataPeriod) => (
                            <tr key={dataPeriod.id} className={dataExtract.data_period_ids.includes(dataPeriod.id) ? 'table-active' : ''}>
                              <td>{`${dataPeriod.district_name} - ${dataPeriod.district_id}`}</td>
                              <td>{dataPeriod.name}</td>
                              <td>{TimePeriodOptions.find((option) => option.value === dataPeriod.time_period)?.label}</td>
                              <td>{moment.utc(dataPeriod.created).format("MMM D, yyyy")}</td>
                              <td>{dataPeriod.user.first_name} {dataPeriod.user.last_name}</td>
                              <td>{dataPeriod.user.current_role ?
                                  UserAssignmentRoleDisplayedNames[dataPeriod.user.current_role as keyof typeof UserAssignmentRoleDisplayedNames] : <span className="text-muted">N/A</span>}
                              </td>
                              <td>
                                {
                                  dataExtract.data_period_ids.includes(dataPeriod.id)
                                  ? <button
                                      type="button"
                                      className="blueBtnSm"
                                      style={{ minWidth: '100px' }}
                                      onClick={() => toggleDataPeriodSelection(dataPeriod.id)}
                                    > <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#ffffff" }} /> Picked</button>
                                  : <button
                                      type="button"
                                      className="whiteBtnSm"
                                      style={{ minWidth: '100px' }}
                                      onClick={() => toggleDataPeriodSelection(dataPeriod.id)}
                                    > Pick</button>
                                }
                              
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </div>
              ) : ''}
            </div>
          </>
          : ''}
        </Form>
      </div>
    </div>
  );
};

export default DataExtractForm;
